import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowIcon } from '../components/icons/ArrowIcon';
import { HeartIcon } from '../components/icons/HeartIcon';
import { Loader } from '../components/Loader';
import { FurnitureModal } from '../components/modal/FurnitureModal';
import { Modal } from '../components/modal/Modal';
import { MoodboardModal } from '../components/modal/MoodboardModal';
import { FurnitureType, RoomFurnitureType, useProject, useUpdateCustomerLike } from '../services/api/useSanity';
import { formatDate } from '../utils/date';
import { numberWithCommas } from '../utils/number';

export const Project: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useProject(slug);
  if (isLoading) {
    return <Loader size={100} />;
  }

  if (!data?._updatedAt) {
    return <>Ingen data og vise</>;
  }
  const furniturePrices = data.rooms_with_furniture
    .filter(room => !!room.room_furniture)
    .map(({ room_furniture }) => ({
      prices: room_furniture.filter(furniture => furniture.customerLike).map(d => d.price * (d.count || 1)),
    }))
    .map(d => d.prices)
    .flat();

  const totalLikedFurniturePrice = furniturePrices.reduce(
    (previousValue, currentValue) => previousValue + currentValue,
    0
  );

  const formatCurrency = (number: number) =>
    Intl.NumberFormat('no-no', {
      notation: 'standard',
      style: 'currency',
      currency: 'NOK',
    }).format(number);

  return (
    <StyledProjectWrapper>
      <div style={{'display': 'flex', 'justifyContent': 'center', 'alignItems': 'center'}}><img src={'/logo_med_gjennomsiktig_bakgrunn__2__720.png'} alt={'PlanMySpace logo'} style={{'width': '80px'}}/>PlanMySpace</div>
      <div
        style={{
          marginBottom: 20,
        }}
      />
      <div className="header">
        <h2>{data.project_name}</h2>
        <p>
          Budsjett:
          <b style={{ marginLeft: 5 }}>{data.budget ? formatCurrency(data.budget) : 0}</b>
        </p>
      </div>
      <p className="last-updated">Sist oppdatert: {formatDate(data._updatedAt)}</p>
      {data?.rooms_with_furniture.map((rwf, index) => (
        <Room key={rwf.room} data={rwf} projectId={data._id} roomIndex={index} />
      ))}
    </StyledProjectWrapper>
  );
};

const Room: FC<{
  data: RoomFurnitureType;
  projectId: string;
  roomIndex: number;
}> = ({ data, projectId, roomIndex }) => {
  const umami = window.umami;
  const [expanded, setExpanded] = useState<boolean>(true);
  const [isMoodboardModalOpen, setIsMoodboardModalOpen] = useState<boolean>(false);
  return (
    <StyledRoom>
      <div onClick={() => setExpanded(!expanded)} className="header">
        <h2>{data.room}</h2>
        <ArrowIcon rotation={expanded ? 0 : 180} />
      </div>
      {expanded && (
        <div>
          {!!data.moodboard_images && (
            <img
              onClick={() => {
                // umami.track('open_moodboard');
                setIsMoodboardModalOpen(!isMoodboardModalOpen);
              }}
              width={`100`}
              style={{ paddingTop: '20px' }}
              className="moodboard-link"
              alt="moodboard"
              src={`${data.moodboard_images[0]}`}
            />
          )}
          {!!data.room_furniture?.length ? (
            <>
              {data.room_furniture.map((furniture, index) => (
                <Furniture
                  key={index}
                  furniture={furniture}
                  roomIndex={roomIndex}
                  projectId={projectId}
                  index={index}
                />
              ))}
            </>
          ) : (
            <p>Ingen møbler her</p>
          )}
        </div>
      )}
      {isMoodboardModalOpen && (
        <Modal closeModal={() => setIsMoodboardModalOpen(!isMoodboardModalOpen)}>
          <MoodboardModal imageUrls={data.moodboard_images} />
        </Modal>
      )}
    </StyledRoom>
  );
};

const Furniture: FC<{
  furniture: FurnitureType;
  roomIndex: number;
  projectId: string;
  index: number;
}> = ({ furniture, roomIndex, projectId, index }) => {
  const umami = window.umami;
  const updateCustomerLike = useUpdateCustomerLike();
  const handleUpdateLike = (value: boolean) => {
    updateCustomerLike.mutate({
      value,
      roomIndex,
      furnitureIndex: index,
      projectId,
    });
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const openModal = () => {
    // umami.track('open_modal', { furniture_model: furniture?.model_name });
    setIsModalOpen(true);
  };

  return (
    <StyledFurniture key={furniture.model_name}>
      <img onClick={openModal} src={furniture.imageUrl} alt={furniture.model_name} />
      <div onClick={openModal} className="description">
        <div>
          <div>
            <b>{furniture.model_name}</b>
            <span>, x {furniture.count}</span>
          </div>
          <p>{furniture.provider ? furniture.provider.title : ''}</p>
        </div>
        <p>{furniture.description}</p>
        <b>{`Pris: ${numberWithCommas(furniture.price)} kr`}</b>
      </div>
      {isModalOpen && (
        <Modal closeModal={() => setIsModalOpen(false)}>
          <FurnitureModal
            isLoadingLike={updateCustomerLike.isLoading}
            heartClick={() => handleUpdateLike(!furniture.customerLike)}
            furniture={furniture}
          />
        </Modal>
      )}
    </StyledFurniture>
  );
};

const StyledProjectWrapper = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    color:white;
  }
  .last-updated {
    font-size: 12px;
  }
  .total-price {
    display: flex;
    justify-content: end;
    b {
      margin: 0 5px;
    }
  }
`;

const StyledRoom = styled.div`
  margin-bottom: 20px;
  .header {
    color: '#fff';
    border-radius: 10px;
    background-color: #939371;
    padding: 0 20px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
  }
  .moodboard-link {
    cursor: pointer;
    font-weight: 359;
    font-size: 16px;
    line-height: 20px;
    text-decoration-line: underline;
    color: ${({ theme }) => theme.color.interactive};
  }
`;

const StyledFurniture = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 40px;
  align-items: center;
  height: 180px;
  img {
    cursor: pointer;
    width: 100%;
    aspect-ratio: 1;
  }
  .description {
    padding: 10px;
    div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      b {
        margin: auto 0;
      }
      p {
        color: #899598;
        font-size: 12px;
      }
    }
    margin-left: 10px;
    p {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3; /* number of lines to show */
      -webkit-box-orient: vertical;
    }
    b {
      align-self: flex-end;
    }
  }
`;
